import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { APIHOST } from '../common';

type Occupant = { id: string; firstName: string; lastName: string };
type ConferenceHotelRoom = { name?: string };
type Order = { documentId: string; confirmation?: string | null };
type Room = {
  id: string;
  confirmation?: string | null;
  dailyRate?: number;
  arrivalDate?: string;
  departureDate?: string;
  type?: string;
  conference_hotel_room?: ConferenceHotelRoom | null;
  order_room_occupants?: Occupant[];
  order?: Order | null;
};

const RoomPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!roomId) return;
    setLoading(true);
    fetch(`${APIHOST}/api/hotel-admin/order-rooms/${roomId}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch room');
        return res.json();
      })
      .then((data) => {
        setRoom(data?.data || null);
        setError(null);
      })
      .catch((err) => {
        setError(err.message);
        setRoom(null);
      })
      .finally(() => setLoading(false));
  }, [roomId]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!room) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="warning">No room data found.</Alert>
      </Container>
    );
  }

  const rows = [
    { label: 'ID', value: room.confirmation || room.id },
    { label: 'Daily Rate', value: typeof room.dailyRate === 'number' ? `$${room.dailyRate.toFixed(2)}` : room.dailyRate },
    { label: 'Arrival Date', value: room.arrivalDate ? new Date(room.arrivalDate).toLocaleDateString() : '—' },
    { label: 'Departure Date', value: room.departureDate ? new Date(room.departureDate).toLocaleDateString() : '—' },
    { label: 'Type', value: room.type ? room.type.charAt(0).toUpperCase() + room.type.slice(1) : '—' },
    { label: 'Hotel Room Name', value: room.conference_hotel_room?.name || '—' },
  ];

  return (
    <Container sx={{ py: 8}}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Room Details
      </Typography>

      {room.order && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{float: 'right'}}>
              <Button
                size="small"
                variant="outlined"
                href={`/hotel-admin/orders/${room.order!.documentId}`}
              >
                View Order
              </Button>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Order #{room.order.confirmation || '—'}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Table size="small">
            <TableBody>
              {rows.map(({ label, value }) => (
                <TableRow key={label} sx={{ border: 'none' }}>
                  <TableCell sx={{ fontWeight: 600, width: '20%', border: 'none' }}>
                    {label}
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ px: 2, py: 1.5}}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Occupants
            </Typography>
          </Box>
          <Table size="small">
            <TableBody>
              {Array.isArray(room.order_room_occupants) && room.order_room_occupants.length > 0 ? (
                room.order_room_occupants.map((occ) => (
                  <TableRow key={occ.id}>
                    <TableCell sx={{border: 'none'}}>{occ.firstName} {occ.lastName}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell sx={{border: 'none'}}>
                    <Typography variant="body2" color="text.secondary">No occupants</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Container>
  );
};

export default RoomPage;
